import {FunnelIcon} from "@heroicons/react/20/solid";
import {useState, useCallback, RefObject, Dispatch, SetStateAction} from "react";
import {createEmptyFilter, Filter, isFilterValueEmpty} from "../../../../hooks/filter/filterState";
import {AnyFilterDefinitions} from "../../../../hooks/filter/filterConfig";
import {generateFilterKey} from "./filterKey";
import {typedMemo} from "../../../../utils/typescript";
import {MenuContent, MenuItem, MenuRoot, MenuTrigger} from "../../../ui/menu";
import {Button} from "../../../ui/button";
import {Icon, IconButton, MenuSelectionDetails, Presence} from "@chakra-ui/react";
import {VisibleFilterEntry} from "./FilterEditor";
import {FacetCount} from "platformed-client/types";
import T from "platformed-components/i18n/T";

export const AddFilterMenu = typedMemo(
  <Ds extends AnyFilterDefinitions>({
    definitions,
    visibleEntries,
    setVisibleEntries,
    menuAnchorRef,

    open,
    onOpenChange,

    hiddenFilters,
    facetCounts,
    context,
    onExitComplete,
  }: {
    definitions: Ds;
    visibleEntries: VisibleFilterEntry<Ds>[];
    setVisibleEntries: Dispatch<SetStateAction<VisibleFilterEntry<Ds>[]>>;
    menuAnchorRef: RefObject<HTMLDivElement>;

    open: boolean;
    onOpenChange: (open: boolean) => void;

    hiddenFilters?: (keyof Ds)[];
    facetCounts: {
      [K in keyof Ds]?: FacetCount[];
    };
    context: object;
    onExitComplete?: () => void;
  }) => {
    type Selected = VisibleFilterEntry<Ds> & {
      /** The `Selected` state intentionally duplicates the selected entry. This
       * allows the menu to continue displaying the last selected state during
       * the brief animation period when the menu is closing and the actual
       * state has been removed. */
      initialFilter: Filter<Ds[keyof Ds]>;
    };

    const [selected, setSelected] = useState<Selected | null>(null);
    const SelectedPickerComponent = selected != null ? definitions[selected.entry.kind]?.components.Picker : null;

    const onSelect = useCallback(
      (d: MenuSelectionDetails) => {
        const key = generateFilterKey();
        if (selected == null) {
          const instantFilter: Filter<Ds[keyof Ds]> | undefined = definitions[d.value as keyof Ds]?.components.filter;
          const filter = instantFilter ?? createEmptyFilter(definitions[d.value as keyof Ds]);

          const entry: VisibleFilterEntry<Ds> = {key, entry: {kind: d.value, filter}};

          setVisibleEntries(prev => [...prev, entry]);

          if (instantFilter != null) {
            // If we were provided with a filter to apply immediately, there's
            // nothing more to do. Close the menu.
            onOpenChange(false);
          } else {
            // Otherwise, set the selected filter which will display the
            // provided picker component.
            setSelected({...entry, initialFilter: filter});
          }
        } else {
          // No-op: ignore onSelect events coming from menu items in the child
          // picker.
          //
          // We could forward them to the child, but for now it's been fine
          // adding event handlers directly to the items.
        }
      },
      [definitions, onOpenChange, selected, setVisibleEntries],
    );

    const innerExitComplete = useCallback(() => {
      // Wait until the menu has fully closed before resetting the selected
      // state, so it continues to display the last selected state during the
      // brief animation period.
      setSelected(null);
      if (onExitComplete != null) {
        onExitComplete();
      }
    }, [onExitComplete]);

    const innerOnOpenChange = useCallback(
      (open: boolean) => {
        onOpenChange(open);

        if (open) {
          // Because we wait for the menu to fully close (onExitComplete) before
          // resetting the selected state, there is an edge case where the user
          // can open the menu again before it's fully closed (e.g. by clicking
          // the "Filter" again while the menu is already open / closing).
          //
          // Ensure that the menu is reset in this case by clearing the state
          // immediately when the menu is opened.
          setSelected(null);
        } else {
          if (selected == null) {
            return;
          }

          setVisibleEntries(prev =>
            prev.filter(ve => !isFilterValueEmpty(definitions[ve.entry.kind], ve.entry.filter)),
          );
        }
      },
      [onOpenChange, selected, setVisibleEntries, definitions],
    );

    return (
      <MenuRoot
        open={open}
        onOpenChange={d => innerOnOpenChange(d.open)}
        onSelect={onSelect}
        closeOnSelect={false}
        onExitComplete={innerExitComplete}
        lazyMount
        unmountOnExit
        composite={false}
        positioning={{hideWhenDetached: true, getAnchorRect: () => menuAnchorRef.current!.getBoundingClientRect()}}
      >
        <MenuTrigger asChild>
          {visibleEntries.length === 0 ? (
            <Button size="xs" variant="ghost">
              <Icon>
                <FunnelIcon />
              </Icon>
              <T keyName="c.Filter[ellipsis]" />
            </Button>
          ) : (
            <IconButton size="xs" variant="ghost" position="relative">
              <Icon>
                <FunnelIcon />
              </Icon>
            </IconButton>
          )}
        </MenuTrigger>
        <MenuContent>
          {selected != null ? (
            <Presence
              present={selected != null}
              animationName={{_open: "slide-from-left"}}
              animationDuration="faster"
              animationTimingFunction="ease-out"
            >
              {SelectedPickerComponent != null && (
                <SelectedPickerComponent
                  filter={selected.entry.filter}
                  onClose={() => innerOnOpenChange(false)}
                  onApply={action => {
                    const filter = action instanceof Function ? action(selected.entry.filter) : selected.entry.filter;
                    setSelected({...selected, entry: {...selected.entry, filter}});
                    setVisibleEntries(prev =>
                      prev.map(ve => (ve.key === selected.key ? {...ve, entry: {...ve.entry, filter}} : ve)),
                    );
                  }}
                  facet={facetCounts[selected.entry.kind] ?? []}
                  context={context}
                />
              )}
            </Presence>
          ) : (
            Object.entries(definitions)
              .filter(([kind]) => hiddenFilters == null || !hiddenFilters.includes(kind))
              .map(([kind, definition]) => {
                return (
                  <MenuItem key={kind} value={kind}>
                    {definition.icon != null && <Icon asChild>{definition.icon}</Icon>}
                    {definition.label}
                  </MenuItem>
                );
              })
          )}
        </MenuContent>
      </MenuRoot>
    );
  },
);
